import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { UiIcon } from '../components/common/UiIcon';
import { SiteFooterNav } from '../components/common/SiteFooterNav';
import { BUSINESS_DISPLAY, hasReportedEcommerceNumber, isConfiguredBusinessInfo } from '../config/business';
import { COMPLIANCE_CONFIG, POLICY_LAST_UPDATED, isConfiguredEmail } from '../config/compliance';
import { SITE_CONFIG } from '../config/site';

function CornerDecorations() {
  return (
    <>
      <span className="pointer-events-none absolute left-5 top-5 h-6 w-6 border-l border-t border-[#6d5128]/70" />
      <span className="pointer-events-none absolute right-5 top-5 h-6 w-6 border-r border-t border-[#6d5128]/70" />
      <span className="pointer-events-none absolute bottom-5 left-5 h-6 w-6 border-b border-l border-[#6d5128]/70" />
      <span className="pointer-events-none absolute bottom-5 right-5 h-6 w-6 border-b border-r border-[#6d5128]/70" />
    </>
  );
}

function SidePageLayout({
  eyebrow,
  title,
  subtitle,
  children,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#140f0d_0%,#17110f_45%,#0d0a09_100%)] text-[#f3e6ce]">
      <main className="px-4 pb-16 pt-10 sm:px-6 lg:px-8">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-8">
          <header className="rounded-[2rem] border border-[#4a341b] bg-[rgba(17,13,11,0.78)] px-6 py-8 shadow-[0_36px_120px_rgba(0,0,0,0.36)] backdrop-blur-sm sm:px-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-[11px] tracking-[0.42em] text-[#b58a45]">{eyebrow}</p>
                <h1 className="mt-3 text-3xl font-semibold tracking-[0.04em] text-[#fff1d6] sm:text-4xl">
                  {title}
                </h1>
                {subtitle ? (
                  <p className="mt-4 max-w-3xl text-sm leading-7 text-[#d5bc97] sm:text-base">{subtitle}</p>
                ) : null}
              </div>
              <Link
                className="inline-flex items-center gap-2 rounded-full border border-[#6a4d24] px-5 py-2 text-sm text-[#f1d7a0] transition hover:border-[#d5a342] hover:text-[#fff1c8]"
                to="/"
              >
                <UiIcon name="home" size={14} />
                메인으로 이동
              </Link>
            </div>
          </header>

          <section className="relative overflow-hidden rounded-[2rem] border border-[#35271d] bg-[rgba(12,10,9,0.8)] px-6 py-8 shadow-[0_32px_100px_rgba(0,0,0,0.34)] sm:px-8">
            <CornerDecorations />
            <div className="relative z-10 space-y-8">{children}</div>
          </section>

          <footer className="rounded-[2rem] border border-[#35271d] bg-[rgba(12,10,9,0.7)] px-6 py-6 shadow-[0_28px_90px_rgba(0,0,0,0.3)] sm:px-8">
            <SiteFooterNav />
            <p className="mt-4 text-xs tracking-[0.12em] text-[#8f7040]">
              {SITE_CONFIG.serviceNameEn} · Updated {POLICY_LAST_UPDATED}
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-[#3f2b18] bg-[rgba(255,255,255,0.02)] px-5 py-5">
      <h2 className="text-sm tracking-[0.28em] text-[#b58a45]">{title}</h2>
      <div className="mt-4 space-y-3 text-sm leading-7 text-[#e4d0ae]">{children}</div>
    </section>
  );
}

export function OriginsPage() {
  return (
    <SidePageLayout
      eyebrow="SERVICE OVERVIEW"
      title="서비스 소개"
      subtitle="묘안은 질문과 카드 흐름을 바탕으로 현재 상황과 다음 결을 읽어주는 AI 타로 리딩 서비스입니다."
    >
      <Section title="서비스 구조">
        <p>메인 화면에서 스프레드를 고르고, 질문을 선택한 뒤 리딩을 시작합니다.</p>
        <p>리딩은 츄르를 사용해 열리며, 카드 선택과 결과 확인까지 한 흐름으로 이어집니다.</p>
      </Section>
      <Section title="핵심 경험">
        <p>복잡한 입력 없이 질문을 고르고 카드 흐름을 따라가며 결과를 확인할 수 있습니다.</p>
        <p>결과 화면에서는 카드별 해석, 전체 요약, 최종 정리를 차례로 확인합니다.</p>
      </Section>
    </SidePageLayout>
  );
}

export function ManifestoPage() {
  return (
    <SidePageLayout
      eyebrow="READING MANIFESTO"
      title="묘안이 읽는 방식"
      subtitle="묘안은 정답을 단정하기보다 지금의 결을 정리하고 다음 선택을 돕는 방향으로 리딩을 제공합니다."
    >
      <Section title="해석 원칙">
        <p>리딩 결과는 엔터테인먼트와 자기 성찰을 위한 참고용 콘텐츠입니다.</p>
        <p>의료, 법률, 세무, 투자 판단을 대신하는 전문 자문이 아닙니다.</p>
      </Section>
    </SidePageLayout>
  );
}

export function ArchivesPage() {
  return (
    <SidePageLayout
      eyebrow="HOW TO USE"
      title="이용 안내"
      subtitle="스프레드 선택부터 결과 확인까지의 기본 이용 흐름을 안내합니다."
    >
      <Section title="이용 순서">
        <p>1. 스프레드를 선택합니다.</p>
        <p>2. 질문을 선택하거나 정리합니다.</p>
        <p>3. 보유 츄르를 확인한 뒤 리딩을 시작합니다.</p>
        <p>4. 카드 선택, 스프레드 배치, 결과 확인 순서로 이어집니다.</p>
      </Section>
      <Section title="츄르 사용">
        <p>리딩은 츄르를 차감해 여는 포인트형 구조입니다.</p>
        <p>부족한 경우 카카오페이로 츄르를 충전한 뒤 같은 흐름으로 바로 이어갈 수 있습니다.</p>
      </Section>
    </SidePageLayout>
  );
}

export function CreditsPage() {
  return (
    <SidePageLayout
      eyebrow="CREDITS"
      title="제작 정보"
      subtitle="서비스 제작과 운영에 관한 기본 정보를 안내합니다."
    >
      <Section title="운영 주체">
        <p>상호: {BUSINESS_DISPLAY.companyName}</p>
        <p>대표자: {BUSINESS_DISPLAY.ceoName}</p>
        <p>호스팅 서비스: {BUSINESS_DISPLAY.hostingProvider}</p>
      </Section>
      <Section title="서비스 목적">
        <p>질문과 카드 해석을 바탕으로 사용자가 현재 흐름을 정리할 수 있도록 돕는 디지털 타로 리딩 서비스입니다.</p>
      </Section>
    </SidePageLayout>
  );
}

export function LegalPage() {
  return <TermsPage />;
}

export function TermsPage() {
  return (
    <SidePageLayout
      eyebrow="TERMS"
      title="이용약관"
      subtitle={`최종 수정일: ${POLICY_LAST_UPDATED}`}
    >
      <Section title="서비스 이용">
        <p>묘안은 로그인 후 츄르를 충전하고, 보유 츄르를 사용해 디지털 타로 리딩을 이용하는 서비스입니다.</p>
        <p>리딩 결과는 결제 및 차감이 완료된 뒤 즉시 제공됩니다.</p>
      </Section>
      <Section title="이용자 유의사항">
        <p>결과는 참고용 콘텐츠이며, 특정 결과를 보장하지 않습니다.</p>
        <p>불법행위, 비정상 트래픽, 타인 권리 침해 행위는 제한됩니다.</p>
      </Section>
      <Section title="문의">
        <p>문의 이메일: {COMPLIANCE_CONFIG.contactEmail}</p>
      </Section>
    </SidePageLayout>
  );
}

export function PrivacyPage() {
  return (
    <SidePageLayout
      eyebrow="PRIVACY"
      title="개인정보처리방침"
      subtitle={`최종 수정일: ${POLICY_LAST_UPDATED}`}
    >
      <Section title="수집 항목">
        <p>로그인 계정 정보, 결제 및 충전 기록, 리딩 이용 기록, 문의 대응에 필요한 정보가 처리될 수 있습니다.</p>
      </Section>
      <Section title="이용 목적">
        <p>계정 인증, 츄르 지갑 운영, 결제 처리, 리딩 제공, 고객 문의 대응, 운영 안정성 확보를 위해 사용합니다.</p>
      </Section>
      <Section title="보관 및 해외 이전">
        <p>분석 데이터 보관 기간: {COMPLIANCE_CONFIG.analyticsRetentionPeriod}</p>
        <p>해외 이전 가능 국가: {COMPLIANCE_CONFIG.overseasCountry}</p>
        <p>수탁·수령자: {COMPLIANCE_CONFIG.overseasRecipient}</p>
      </Section>
    </SidePageLayout>
  );
}

export function ContactPage() {
  return (
    <SidePageLayout
      eyebrow="CONTACT"
      title="문의"
      subtitle="서비스 이용, 츄르 충전, 환불, 계정 관련 문의를 아래 연락처로 접수할 수 있습니다."
    >
      <Section title="연락처">
        <p>이메일: {BUSINESS_DISPLAY.email}</p>
        <p>전화: {BUSINESS_DISPLAY.phone}</p>
      </Section>
      <Section title="안내">
        <p>문의 시 계정 이메일, 문제 발생 시점, 사용 기기나 브라우저 정보가 함께 있으면 확인이 빠릅니다.</p>
        {!isConfiguredEmail ? <p>문의 이메일 설정이 아직 완료되지 않았습니다.</p> : null}
      </Section>
    </SidePageLayout>
  );
}

export function RefundPage() {
  return (
    <SidePageLayout
      eyebrow="REFUND POLICY"
      title="환불정책"
      subtitle="츄르 충전과 사용에 대한 환불 및 복구 기준을 안내합니다."
    >
      <Section title="미사용 츄르">
        <p>결제 오류, 중복 결제, 미사용 잔액은 확인 후 환불 또는 잔액 복구가 가능합니다.</p>
      </Section>
      <Section title="사용된 츄르">
        <p>보유 츄르를 사용해 리딩을 시작하고 결과가 제공된 경우, 디지털 콘텐츠 이용이 개시된 것으로 봅니다.</p>
        <p>이 경우 전자상거래법 및 디지털 콘텐츠 특성상 환불이 제한될 수 있습니다.</p>
      </Section>
      <Section title="처리 절차">
        <p>환불 문의는 {BUSINESS_DISPLAY.email} 또는 {BUSINESS_DISPLAY.phone}로 접수할 수 있습니다.</p>
      </Section>
    </SidePageLayout>
  );
}

export function BusinessInfoPage() {
  return (
    <SidePageLayout
      eyebrow="BUSINESS INFORMATION"
      title="사업자 정보"
      subtitle="전자상거래법에 따른 사업자 및 통신판매업 관련 정보를 안내합니다."
    >
      <Section title="기본 정보">
        <p>상호: {BUSINESS_DISPLAY.companyName}</p>
        <p>대표자: {BUSINESS_DISPLAY.ceoName}</p>
        <p>사업자등록번호: {BUSINESS_DISPLAY.businessNumber}</p>
        <p>주소: {BUSINESS_DISPLAY.address}</p>
        <p>연락처: {BUSINESS_DISPLAY.phone}</p>
        <p>이메일: {BUSINESS_DISPLAY.email}</p>
        <p>호스팅 서비스: {BUSINESS_DISPLAY.hostingProvider}</p>
        {hasReportedEcommerceNumber ? (
          <p>통신판매업 신고번호: {BUSINESS_DISPLAY.ecommerceNumber}</p>
        ) : null}
      </Section>
      {!isConfiguredBusinessInfo ? (
        <Section title="안내">
          <p>일부 사업자 정보 설정이 완료되지 않았습니다. 운영 전까지 최신 정보로 갱신합니다.</p>
        </Section>
      ) : null}
    </SidePageLayout>
  );
}
